'use strict';
var __awaiter =
  (this && this.__awaiter) ||
  function (thisArg, _arguments, P, generator) {
    function adopt(value) {
      return value instanceof P
        ? value
        : new P(function (resolve) {
            resolve(value);
          });
    }
    return new (P || (P = Promise))(function (resolve, reject) {
      function fulfilled(value) {
        try {
          step(generator.next(value));
        } catch (e) {
          reject(e);
        }
      }
      function rejected(value) {
        try {
          step(generator['throw'](value));
        } catch (e) {
          reject(e);
        }
      }
      function step(result) {
        result.done
          ? resolve(result.value)
          : adopt(result.value).then(fulfilled, rejected);
      }
      step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
  };
var __importDefault =
  (this && this.__importDefault) ||
  function (mod) {
    return mod && mod.__esModule ? mod : { default: mod };
  };
Object.defineProperty(exports, '__esModule', { value: true });
const react_1 = require('react');
const _helpers_1 = require('../components/Inputs/_helpers');
const form_1 = require('../contexts/form');
const useStatusHandler_1 = __importDefault(require('./useStatusHandler'));
// A request that got no response at all (Safari "Load failed", Chrome "Failed
// to fetch", Firefox "NetworkError…"): no GraphQL error, no validation
// failure, nothing the server said.
function isNetworkDrop(e) {
  var answered = e && e.response && e.response.errors && e.response.errors[0];
  return (
    !answered &&
    /^(Load failed|Failed to fetch|NetworkError|Network request failed|The network connection was lost)/i.test(
      (e && e.message) || String(e)
    )
  );
}
// How long an autosave waits before its one retry after a network drop.
// ponytail: fixed delay, one retry. Backoff if a phone waking from idle turns
// out to need longer than this to get its connection back.
var NETWORK_RETRY_DELAY = 1000;
// graphql-request's ClientError message is "<the server's first error, or
// 'GraphQL Error (Code: 502)'>: " + JSON.stringify({ response, request }), and
// `request.variables` is what the form was saving: a guest's name, email,
// phone, Test answers. None of that belongs in Rollbar, and with it in the
// title every occurrence became its own item. Cuts that JSON out of a message
// or a stack; it has no raw newline in it, so it ends where its line does.
function withoutRequest(text) {
  return String(text).replace(/: \{"response":[^\n]*/, '');
}
// A failed submit used to be a console.log: the person saw "Something has gone
// wrong", and nothing else ever did. Every form in the app saves through this
// one handler — every builder modal, every guest form — so a save that fails
// reports here, through the browser Rollbar snippet already on the page, and
// Rollbar's new_item rule posts it to #bugs. The first error message is in the
// title, so a repeating cause stays one item rather than one per occurrence.
//
// Returns the message it reported under, so a caller that also counts the
// failure (reportCompleteSaveFailure) says the same thing without working it
// out a second time.
function reportSubmitFailure(e) {
  var gqlError = e && e.response && e.response.errors && e.response.errors[0];
  var message = withoutRequest(
    (gqlError && gqlError.message) || (e && e.message) || String(e)
  );
  if (typeof window === 'undefined') return message;
  if (!window.Rollbar || !window.Rollbar.error) return message;
  // A request that never reached the server is the person's connection, not
  // a bug: onark-frontend #576 was one guest on a phone retrying a form
  // three times in 30s with no network. Still recorded, but as a warning so
  // it does not page #bugs as an error.
  // `expected` marks a failure the person caused and is told about (a wrong
  // password), so it is a warning too.
  var report =
    (isNetworkDrop(e) || (e && e.expected)) && window.Rollbar.warning
      ? window.Rollbar.warning
      : window.Rollbar.error;
  // The cause goes in custom data, NOT as the Error argument: handed the
  // error, Rollbar titles the item after its class, which minifies to
  // something like "l: rc-form submit failed: …" and reads as noise in Slack.
  report.call(window.Rollbar, 'rc-form submit failed: ' + message, {
    code: gqlError && gqlError.extensions && gqlError.extensions.code,
    // Chrome starts an Error's stack with its message, request and all.
    stack: e && e.stack && withoutRequest(e.stack),
    submitError: message,
  });
  return message;
}
const useSubmitHandler = ({
  autoSave,
  formDataType = 'object',
  onSubmit,
  onSuccess,
}) => {
  const { changedData, fields, initialData, setState } = (0,
  form_1.useInternalForm)();
  const changeStatus = (0, useStatusHandler_1.default)();
  // What the form has done since a submit started, for the retry below.
  const latest = (0, react_1.useRef)({ run: 0 });
  latest.current.changedData = changedData;
  return ev =>
    __awaiter(void 0, void 0, void 0, function* () {
      var _a, _b;
      if (ev.preventDefault) ev.preventDefault();
      const run = ++latest.current.run;
      const path = window.location.pathname;
      // since the form was submitting, change the status to pending and reset errors
      changeStatus('PENDING');
      setState(s => Object.assign(Object.assign({}, s), { errors: [] }));
      try {
        const form = ev.target;
        const valid = form.checkValidity();
        // if the form is invalid, set error status
        if (!valid) {
          changeStatus('ERROR');
          setState(s =>
            Object.assign(Object.assign({}, s), {
              errors: ['There are items that require your attention'],
            })
          );
          [...form.querySelectorAll(':invalid')].forEach(input => {
            (0, _helpers_1.checkValidity)({ target: input });
          });
          return;
        }
        // get the form data
        const formData = new FormData(form);
        let data;
        if (formDataType === 'object') {
          if (fields && Object.keys(fields).length) {
            data = JSON.parse(JSON.stringify(fields || {}));
          } else {
            data = Object.fromEntries(formData);
          }
        } else {
          data = formData;
        }
        // get the data that is changed
        const changed = JSON.parse(JSON.stringify(changedData || {}));
        // reset the the changed, so we continue to capture while submiting
        setState(s => Object.assign(Object.assign({}, s), { changedData: {} }));
        // submit the data
        const initial = JSON.parse(JSON.stringify(initialData || {}));
        let info;
        try {
          info = yield onSubmit(data, initial, changed);
        } catch (e) {
          // An autosave that got no response is sent once more before anyone
          // is told. An autosaving form has no Save button and the guest Test
          // and Video sections show no error, so nothing else re-sends it: a
          // phone waking from idle dropped the save behind a guest's answer
          // (onark-frontend #576), and when that is the last answer the guest
          // sits on "Calculating…" until they reload. Only autosaves: they all
          // upsert or update by id, where a manually submitted form can be a
          // create (a second church) or a send (a second reset email), and
          // "no response" does not prove the server never got the first one.
          if (!autoSave || !isNetworkDrop(e)) throw e;
          yield new Promise(res => setTimeout(res, NETWORK_RETRY_DELAY));
          // The retry carries the data as it stood when the save started. If
          // the form has moved on during the wait (a newer save ran, an edit
          // has one scheduled, the person left the page) sending it now would
          // put older data over newer, so it is dropped: an autosave sends the
          // full data, and the newer save carries everything this one did.
          if (
            latest.current.run !== run ||
            Object.keys(latest.current.changedData || {}).length ||
            !form.isConnected ||
            window.location.pathname !== path
          )
            return;
          info = yield onSubmit(data, initial, changed);
        }
        // set the status to success
        changeStatus('SUCCESS');
        // reset after getting data
        setState(s =>
          Object.assign(Object.assign({}, s), {
            fields: info || fields,
            initialData: info || initialData,
          })
        );
        // hide success and clear form
        setTimeout(
          () =>
            __awaiter(void 0, void 0, void 0, function* () {
              if (onSuccess) yield onSuccess(info, form);
              form
                .querySelectorAll('[aria-invalid]')
                .forEach(el => el.removeAttribute('aria-invalid'));
            }),
          1000
        );
      } catch (e) {
        if (
          (_b =
            (_a = e === null || e === void 0 ? void 0 : e.response) === null ||
            _a === void 0
              ? void 0
              : _a.errors) === null || _b === void 0
            ? void 0
            : _b.length
        ) {
          const errors = e.response.errors
            .map(err =>
              err.extensions.code === 'INTERNAL_SERVER_ERROR'
                ? 'Something has gone wrong, please try again.'
                : err.message
            )
            .filter(Boolean);
          setState(s =>
            Object.assign(Object.assign({}, s), { errors: errors })
          );
        } else {
          setState(s =>
            Object.assign(Object.assign({}, s), {
              errors: ['Something has gone wrong, please try again.'],
            })
          );
        }
        // Put the diff back. It is cleared BEFORE the submit so edits made
        // while the request is in flight survive a success — but on a failure
        // that left the consumer's `changedValues` empty, so pressing Save
        // again sent nothing and "please try again" could never work. This
        // closure's `changedData` is the diff as it stood when the submit
        // started; anything typed since is in `s.changedData` and wins.
        //
        // Not for an autosaving form: restoring the diff re-runs the effect
        // that schedules the autosave, so a form saving on a timer would retry
        // for as long as the failure lasts. Those forms all submit the FULL
        // data rather than the diff, so they lose nothing here.
        if (!autoSave)
          setState(s =>
            Object.assign(Object.assign({}, s), {
              changedData: Object.assign(
                Object.assign({}, changedData),
                s.changedData
              ),
            })
          );
        reportSubmitFailure(e);
        changeStatus('ERROR');
      }
    });
};
exports.default = useSubmitHandler;
// For the saves that do not go through this handler (a guest section's
// COMPLETE save), so they report the same way under the same Rollbar item.
exports.reportSubmitFailure = reportSubmitFailure;
//# sourceMappingURL=useSubmitHandler.js.map
